import { Injectable } from '@angular/core';
import { cloneDeep } from '@apollo/client/utilities';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  static cloneObject<T>(source: T) {
    return cloneDeep(source);
  }

  static groupBy<T>(array: T[], property: string) {
    return array.reduce((acc: { [key: string]: T[] }, obj) => {
      const key = obj[property];

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(obj);
      return acc;
    }, {});
  }

  /*----------------------------------------------------------------------------------

	SORT ASCENDING

	- ACCEPTS:
	1.) array
	2.) function containing value to be sorted

	- EXAMPLE:
	UtilityService.sortAsc(array, function(o){ return o.Timestamp })

	- WHERE:
	array is the array containing values to sorted
	function(o){ return o.Timestamp } is the function used by sorting algoritm.  Timestamp is property of items in array used to sort

	- NOTE:
	SEE INDEX.HTML PAGE FOR DIFFERENT SORT PROTOTYPES
	----------------------------------------------------------------------------------*/
  static sortAsc(array, func) {
    return array.sortByAsc(func);
  }

  /*----------------------------------------------------------------------------------

	SORT DESCENDING

	- ACCEPTS:
	1.) array
	2.) function containing value to be sorted

	- EXAMPLE:
	UtilityService.sortDesc(array, function(o){ return o.Timestamp })

	- WHERE:
	array is the array containing values to sorted
	function(o){ return o.Timestamp } is the function used by sorting algoritm.  Timestamp is property of items in array used to sort

	- NOTE:
	SEE INDEX.HTML PAGE FOR DIFFERENT SORT PROTOTYPES
	----------------------------------------------------------------------------------*/
  static sortDesc(array, func) {
    return array.sortByDesc(func);
  }

  static roundUpToNearest5(value: number) {
    return UtilityService.roundUp(value, 5);
  }

  static roundUp(value: number, coefficient: number) {
    return Math.ceil(value / coefficient) * coefficient;
  }

  static getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  static generatePassword(): string {
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const specialChars = '!@#$%^&*()_+-=[]{}|;:,.<>?';

    let password = '';

    password +=
      uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
    password +=
      lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    const allChars = uppercaseChars + lowercaseChars + numbers + specialChars;
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    return password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');
  }

  static parseMarkdown(content: string): string {
    return content
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/^\|(.+)\|$/gm, (match) => {
        const cells = match.split('|').slice(1, -1);
        return `<tr>${cells.map((cell) => `<td>${cell.trim()}</td>`).join('')}</tr>`;
      })
      .replace(/^---$/gm, '<hr>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/^(?!<[h1-6]|<table|<tr|<hr)(.+)$/gm, '<p>$1</p>');
  }

  static getTimeUntilResetString(resetTime: string): string {
    const timeDiff = new Date(resetTime).getTime() - new Date().getTime();
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const timeUntilReset = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    return timeUntilReset;
  }
}

/*
ARRAY SORT FUNCTIONS

SORT AN ARRAY ASCENDING OR DESCENDING

REFER TO:
https://stackoverflow.com/questions/10123953/how-to-sort-an-object-array-by-date-property/10124053#10124053
https://stackoverflow.com/a/41413433/418549

sortBy function uses Schwartzian transform (https://en.wikipedia.org/wiki/Schwartzian_transform) on all arrays

*/
declare global {
  interface Array<T> {
    sortByAsc(f: (item: T) => any): Array<T>;
    sortByDesc(f: (item: T) => any): Array<T>;
  }
}

if (!Array.prototype.sortByAsc) {
  // Non-enumerable, or every `for...in` over an array walks into it (the
  // TermsFeed cookie script does, and threw on every page).
  Object.defineProperty(Array.prototype, 'sortByAsc', {
    configurable: true,
    writable: true,
    enumerable: false,
    value: function sbAsc(this: any[], f: (item: any) => any): any[] {
      for (let i = this.length; i; ) {
        const o = this[--i];
        this[i] = [].concat(f.call(o, o, i), o);
      }

      this.sort(function (a, b) {
        for (let i = 0, len = a.length; i < len; ++i) {
          if (a[i] !== b[i]) {
            return a[i] < b[i] ? -1 : 1;
          }
        }
        return 0;
      });

      for (let i = this.length; i; ) {
        this[--i] = this[i][this[i].length - 1];
      }

      return this;
    }
  });
}

if (!Array.prototype.sortByDesc) {
  // Non-enumerable, or every `for...in` over an array walks into it (the
  // TermsFeed cookie script does, and threw on every page).
  Object.defineProperty(Array.prototype, 'sortByDesc', {
    configurable: true,
    writable: true,
    enumerable: false,
    value: function sbDesc(this: any[], f: (item: any) => any): any[] {
      for (let i = this.length; i; ) {
        const o = this[--i];
        this[i] = [].concat(f.call(o, o, i), o);
      }

      this.sort(function (a, b) {
        for (let i = 0, len = a.length; i < len; ++i) {
          if (a[i] !== b[i]) {
            return a[i] > b[i] ? -1 : 1;
          }
        }
        return 0;
      });

      for (let i = this.length; i; ) {
        this[--i] = this[i][this[i].length - 1];
      }

      return this;
    }
  });
}
